import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { API_BASE_URL } from "../config";
import { setError } from "./errorSlice"; // Optional if you use global error handling

export const initialState = {
  subjects: [],
  studentInfo: null, // will hold studentId, studentName, batchId, etc.
  status: "idle",
  error: null,
  loading: false,
  success: false,
};

// Helper function to extract token
const getToken = (dispatch, rejectWithValue) => {
  const storedUser = localStorage.getItem("userInfo");

  if (!storedUser) {
    const errorMsg = "Session expired. Please log in again.";
    dispatch?.(setError(errorMsg));
    return rejectWithValue(errorMsg);
  }

  const { token } = JSON.parse(storedUser);

  if (!token) {
    const errorMsg = "Session expired. Please log in again.";
    dispatch?.(setError(errorMsg));
    return rejectWithValue(errorMsg);
  }

  return token;
};

// Fetch Student Subjects
export const fetchStudentSubjects = createAsyncThunk(
  "dashboard/fetchStudentSubjects",
  async (studentId, { rejectWithValue, dispatch }) => {
    try {
      const token = getToken(dispatch, rejectWithValue);
      if (typeof token !== "string") return token;

      const response = await fetch(
        `${API_BASE_URL}/api/Student/subjects/${studentId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Validate schema (studentInfo + subjects[])
      if (!data || !Array.isArray(data.subjects)) {
        throw new Error("Invalid student subjects response format");
      }

      return data;
    } catch (error) {
      console.error("Error fetching student subjects:", error);
      dispatch(setError(error.message));
      return rejectWithValue(error.message || "Failed to fetch student subjects");
    }
  }
);

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Student Subjects
      .addCase(fetchStudentSubjects.pending, (state) => {
        state.status = "loading";
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(fetchStudentSubjects.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loading = false;
        state.success = true;
        state.studentInfo = {
          studentId: action.payload.studentId,
          studentName: action.payload.studentName,
          batchId: action.payload.batchId,
          batchName: action.payload.batchName,
          sectionId: action.payload.sectionId,
          sectionName: action.payload.sectionName,
        };
        state.subjects = action.payload.subjects;
      })
      .addCase(fetchStudentSubjects.rejected, (state, action) => {
        state.status = "failed";
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      });
  },
});

export const { clearDashboardError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
